import api from './api'

export interface CrmIntegrationSettings {
    configured: boolean
    connected: boolean
    connection_detail: string
    sources_refresh_minutes: number
    leads_refresh_minutes: number
    leads_limit: number
    auto_pull_enabled: boolean
    push_assignee_enabled: boolean
    push_assignee_overwrite: boolean
    push_status_enabled: boolean
    push_notes_enabled: boolean
    push_postpone_comment_enabled: boolean
    // Комментарий с именем сотрудника после смены статуса, полей и назначения.
    push_action_comments_enabled: boolean
    // Загружать лидов, за которыми в CRM Wiseway уже закреплён ответственный.
    pull_with_assignee: boolean
    // Статусы CRM Wiseway, по которым забираем лидов.
    pull_statuses: string[]
    // Правило SLA по первому сообщению и его срок в часах.
    sla_no_answer_enabled: boolean
    sla_no_answer_hours: number
    sla_no_answer_minutes: number
    // Гео как условие загрузки лидов. Пусто - «Все гео».
    pull_geos: string[]
    // Задачи из CRM Wiseway: импорт и отслеживание изменений.
    pull_tasks_enabled: boolean
    watch_tasks_enabled: boolean
    pull_tasks_own_leads_only: boolean
    pull_tasks_skip_titles: string[]
    pull_tasks_unassigned_mode: 'unassigned' | 'skip'
    // Забирать и уже существующие задачи по нашим лидам.
    pull_tasks_by_leads: boolean
    // Передача задач в их CRM и закрытие там же.
    push_tasks_enabled: boolean
    close_tasks_in_crm_enabled: boolean
    // Поля карточки лида: чтение их правок и передача наших.
    pull_lead_fields_enabled: boolean
    push_lead_fields_enabled: boolean
    last_lead_fields_sync_at: string | null
    last_crm_task_id: number | null
    last_tasks_sync_at: string | null
    // Сколько рабочих TG-аккаунтов в каждом пуле температур.
    pool_account_counts: Record<string, number>
    last_sources_sync_at: string | null
    last_leads_sync_at: string | null
    last_error: string | null
}

export type SourceTemperature = 'cold' | 'warm' | 'hot'

export interface CrmLeadSource {
    id: number
    name: string
    enabled: boolean
    // Через какой пул TG-аккаунтов уходят лиды этого источника.
    temperature: SourceTemperature
    last_seen_at: string | null
}

// Что показывается в окне поиска до того, как лида заведут.
export interface CrmCardPreview {
    crm_ww_id: string
    title: string
    source: string | null
    geo: string | null
    crm_status: string | null
    telegram: string | null
    phone: string | null
    contacts_hidden: boolean
    has_telegram: boolean
    temperature: string
    assignee_name: string | null
    // Профиль видимости, в который попадёт лид по ответственному из CRM.
    profile_name?: string | null
    // Причина, по которой добавить нельзя совсем.
    blocker: string | null
    // Тот же человек уже заведён под другим номером карточки.
    duplicate: { lead_id: number; title: string } | null
    can_add: boolean
}

export interface PullStatusGroup {
    group: string
    reasons: Array<{ code: string; label: string }>
}

// Интеграция с CRM Wiseway. Настройки общие на систему, доступны тимлиду и
// суперадмину.
export interface SkippedLead {
    id: number
    crm_ww_id: string
    reason: string
    title: string | null
    telegram_contact: string | null
    phone: string | null
    source: string | null
    lead_id: number | null
    lead_title: string | null
    created_at: string
}

export interface PullResult {
    fetched: number
    created: number
    skipped_no_telegram: number
    skipped_duplicate: number
    failed: number
    // Сколько лидов ещё осталось забрать по включённым источникам.
    remaining: number
    // Сколько лидов приехали закреплёнными за нашим сотрудником и у скольких
    // ответственного из CRM не нашлось по почте.
    assigned: number
    assignee_not_found: number
    duplicates: Array<{
        crm_ww_id: string
        title: string | null
        telegram_contact: string | null
        phone: string | null
        reason: string
        lead_id: number
        lead_title: string | null
    }>
}

export const crmIntegrationApi = {
    getSettings: () => api.get<CrmIntegrationSettings>('/crm-integration/settings'),

    updateSettings: (data: Partial<Pick<CrmIntegrationSettings,
        'sources_refresh_minutes' | 'leads_refresh_minutes' | 'leads_limit' | 'auto_pull_enabled'
        | 'push_assignee_enabled' | 'push_assignee_overwrite' | 'push_status_enabled' | 'push_notes_enabled' | 'push_postpone_comment_enabled' | 'push_action_comments_enabled'
        | 'pull_tasks_enabled' | 'watch_tasks_enabled' | 'pull_tasks_own_leads_only' | 'pull_tasks_by_leads'
        | 'push_tasks_enabled' | 'close_tasks_in_crm_enabled'
        | 'pull_lead_fields_enabled' | 'push_lead_fields_enabled'
        | 'pull_with_assignee' | 'pull_statuses' | 'sla_no_answer_enabled' | 'sla_no_answer_hours' | 'sla_no_answer_minutes' | 'pull_geos'>>) =>
        api.put('/crm-integration/settings', data),

    getPullStatuses: () => api.get<{ groups: PullStatusGroup[] }>('/crm-integration/pull-statuses'),

    getSources: () => api.get<CrmLeadSource[]>('/crm-integration/sources'),

    // Обновляет справочник источников и заодно пополняет список гео.
    refreshSources: () =>
        api.post<{ success: boolean; count: number; geo_added: number }>('/crm-integration/sources/refresh'),

    pullLeads: (limit?: number) =>
        api.post<PullResult>('/crm-integration/leads/pull', { limit }),

    // Карточка CRM Wiseway по номеру: показать до создания лида.
    previewCrmCard: (crmWwId: string) =>
        api.get<CrmCardPreview>(`/crm-integration/leads/crm-card/${encodeURIComponent(crmWwId)}`),

    // Завести лида по идентификатору его карточки в CRM Wiseway.
    addLeadByCrmId: (crmWwId: string, ignoreDuplicate = false) =>
        api.post<{ id: number; title: string; source: string | null; temperature: string; crm_status: string | null }>(
            '/crm-integration/leads/by-crm-id', { crm_ww_id: crmWwId, ignore_duplicate: ignoreDuplicate }),

    // Раздел «Дубли»: лиды CRM Wiseway, которых мы не загрузили.
    getDuplicates: () => api.get<SkippedLead[]>('/crm-integration/duplicates'),

    deleteDuplicate: (id: number) => api.delete(`/crm-integration/duplicates/${id}`),

    updateSource: (sourceId: number, data: { enabled?: boolean; temperature?: SourceTemperature }) =>
        api.put<{ id: number; enabled: boolean; temperature: SourceTemperature; retagged: number }>(
            `/crm-integration/sources/${sourceId}`, data),
}
